<script setup lang="ts">
import { storeToRefs } from "pinia";
import { computed, onMounted } from "vue";
import { useI18n } from "vue-i18n";

import { applyActionParams } from "@/action/actions";
import { boardActions } from "@/action/boardActions";
import { cardActions } from "@/action/cardActions";
import { selectionActions } from "@/action/selectionActions";
import type { SelectionScope } from "@/action/selectionActions";
import { toggleActions } from "@/action/toggleActions";
import { action, actionMap, actionMapWith } from "@/components/action/action";
import { actionSourceKey } from "@/components/action/injectKeys";
import SubMenu from "@/components/ui/DropdownMenu/SubMenu.vue";
import MenuList from "@/components/ui/MenuList/MenuList.vue";
import MenuListItem from "@/components/ui/MenuList/MenuListItem.vue";
import MenuListItems from "@/components/ui/MenuList/MenuListItems.vue";
import MenuListSeparator from "@/components/ui/MenuList/MenuListSeparator.vue";
import MenuListSubheader from "@/components/ui/MenuList/MenuListSubheader.vue";
import SvgIcon from "@/components/ui/SvgIcon/SvgIcon.vue";
import { keyboardNavigation } from "@/directives/keyboardNavigation";
import { relativeFromViewport } from "@/math/coordinate-utils";
import { DAYS } from "@/model/Settings";
import type { ViewportCoordinate } from "@/model/coordinates";
import { useBoardStore } from "@/store/board";
import { useSelectionStore } from "@/store/selection";
import { useUserStore } from "@/store/user";
import { useWorkModeStore } from "@/store/workMode";
import { injectStrict } from "@/utils/vue";

export type BoardContextMenuMode = "iteration" | "backlog" | "mouse";

const props = defineProps<{ position: ViewportCoordinate; mode: BoardContextMenuMode }>();

defineEmits<{ dummy: [] }>();

onMounted(() => {
  useSelectionStore().checkUndo();
});

const vKeyboardNavSub = keyboardNavigation();

const actionSource = injectStrict(actionSourceKey);

const selectedCount = computed(() => useBoardStore().selectedStickyIds.length);
const editable = computed(() => useUserStore().isAllowed("edit"));
const canCreateStickies = computed(() => useBoardStore().creatableStickyTypes.length > 0);
const context = computed(() => useBoardStore().baseBoard.contextActions);
const positional = computed(() =>
  useBoardStore().baseBoard.positionalActions(relativeFromViewport(props.position)),
);
const general = computed(() =>
  actionMap(
    {
      newSticky:
        editable.value && canCreateStickies.value && props.mode === "mouse"
          ? applyActionParams(cardActions.add, { pos: props.position }, true)
          : undefined,
      screenshot:
        props.mode === "mouse" || props.mode === "backlog" ? boardActions.screenshot : undefined,
    },
    { source: actionSource },
  ),
);
const anyGeneral = computed(() => Object.values(general.value).some((value) => value));
const { isExecutionMode } = storeToRefs(useWorkModeStore());
const isTeamBoard = computed(() => useBoardStore().board?.type === "team");
const canShowKanbanOrMyWork = computed(() => !isTeamBoard.value || isExecutionMode.value);

const board = computed(() => actionMap(context.value.board, { source: actionSource }));
const region = computed(() =>
  actionMap(positional.value.region?.actions, { source: actionSource }),
);
const onSelection = actionMapWith<[SelectionScope]>("selection")(selectionActions);
const onRegion = actionMapWith<[SelectionScope]>("region")(selectionActions);

const { t } = useI18n();

const lastAction = computed(() => useSelectionStore().lastHistoryEntry);
const undoText = computed(() => {
  return lastAction.value
    ? t("action.undoSelectionAction", {
        action: t(lastAction.value.action.name),
        count: lastAction.value.stateBefore.length,
      })
    : t("action.undo");
});

const newStickyShortcut =
  Math.floor(new Date().getTime() / (7 * DAYS)) % 2 === 0 ? t("mouse.longClick") : "N";
</script>

<template>
  <MenuList>
    <MenuListItems role="menu" style="width: 300px">
      <template v-if="anyGeneral">
        <MenuListSubheader>{{ $t("contextMenu.general") }}</MenuListSubheader>
        <MenuListItem
          v-if="general.newSticky"
          v-bind="general.newSticky"
          :shortcut="newStickyShortcut"
        />
        <MenuListItem v-if="general.screenshot" v-bind="general.screenshot" />
        <MenuListItem
          v-if="canShowKanbanOrMyWork && board.kanban"
          v-bind="board.kanban"
          :icon="null"
        />
        <MenuListItem v-if="board.syncProgramBacklog" v-bind="board.syncProgramBacklog" />
        <MenuListItem v-if="board.draw" v-bind="board.draw" :icon="null" />
      </template>

      <template v-if="editable && selectedCount > 1 && context.selection">
        <SubMenu width="md">
          <template #trigger>
            <MenuListItem :text="$t('contextMenu.selectedStickyCount', { selectedCount })">
              <template #after>
                <SvgIcon name="arrow/chevron-right" width="16" height="16" />
              </template>
            </MenuListItem>
          </template>
          <MenuList>
            <MenuListItems v-keyboard-nav-sub role="menu">
              <MenuListItem v-bind="action(toggleActions.showSelectionOverview)" />
              <template v-if="context.selection.organize">
                <MenuListItem v-bind="onSelection.alignHorizontal" />
                <MenuListItem v-bind="onSelection.distributeHorizontal" />
                <MenuListItem v-bind="onSelection.alignVertical" />
                <MenuListItem v-bind="onSelection.distributeVertical" />
                <MenuListItem v-bind="onSelection.lineup" />
                <MenuListItem v-bind="onSelection.arrange" />
                <MenuListItem v-bind="onSelection.stack" />
              </template>
              <MenuListItem
                v-if="context.selection.mirror"
                v-bind="onSelection.openMirrorModal"
                :disabled="useSelectionStore().mirrorTargets.length === 0"
              />
              <MenuListItem v-if="context.selection.team" v-bind="onSelection.openTeamModal" />
              <MenuListItem v-if="context.selection.art" v-bind="onSelection.openArtModal" />
              <MenuListItem
                v-if="context.selection.link"
                v-bind="action(cardActions.openLinkModal)"
              />
              <MenuListItem
                v-bind="onSelection.share"
                :text="$t('label.actionMenu.stickiesLinkToClipboard')"
              />
            </MenuListItems>
          </MenuList>
        </SubMenu>
      </template>

      <template v-if="positional.region">
        <MenuListSubheader>{{ positional.region.name }}</MenuListSubheader>
        <MenuListItem v-if="region.overview" v-bind="region.overview" />
        <MenuListItem
          v-if="canShowKanbanOrMyWork && region.kanban"
          v-bind="region.kanban"
          :icon="null"
        />
        <MenuListItem v-if="region.zoom" v-bind="region.zoom" />
        <MenuListItem v-if="region.sync" v-bind="region.sync" />
        <SubMenu v-if="editable && selectedCount <= 1 && positional.region.organize" width="lg">
          <template #trigger>
            <MenuListItem :text="$t('actionMenu.organize')">
              <template #after>
                <SvgIcon name="arrow/chevron-right" width="16" height="16" />
              </template>
            </MenuListItem>
          </template>
          <MenuList>
            <MenuListItems v-keyboard-nav-sub role="menu">
              <MenuListItem v-bind="onRegion.alignHorizontal" />
              <MenuListItem v-bind="onRegion.distributeHorizontal" />
              <MenuListItem v-bind="onRegion.alignVertical" />
              <MenuListItem v-bind="onRegion.distributeVertical" />
              <MenuListItem v-bind="onRegion.lineup" />
              <MenuListItem v-bind="onRegion.arrange" />
              <MenuListItem v-bind="onRegion.stack" />
            </MenuListItems>
          </MenuList>
        </SubMenu>
      </template>

      <template v-if="lastAction">
        <MenuListSeparator />
        <MenuListItem v-bind="action(selectionActions.undo)" :text="undoText" :icon="null" />
      </template>
    </MenuListItems>
  </MenuList>
</template>

<style lang="scss">
@use "@/styles/mixins/shortcut";

.btn {
  @include shortcut.key;
}
</style>
